'use client';

import {
    clsx,
    getStoreDisplayTime,
    storeIsOpen,
    usePageLayout,
    useUserSession,
    PageType,
    useCart,
    ExperimentVersion,
    useExperimentValue,
    getStoresByQuery,
    getStoresByIPAddress,
    getStoresByGeolocation,
    getClosesStoresByCoordinates,
} from '@libs/common-next';

import { DeliveryType, getLocale, NAVIGATION_URLS } from '@libs/core';
import {
    List,
    StockStatusEnum,
    StoreListingCard,
    StoreSearchDrawer,
} from '@petsmart-ui/sparky';
import { useCallback, useEffect, useState } from 'react';
import type { InventoryHitStore } from '@libs/algolia';
import type {
    InventoryResult,
    StoreSearchResult,
    UpdateItemFulfillmentMethodRequestBody,
} from '@libs/common-node';

import type { StoreDrawerProps } from './types';
import {
    addStoreSkuInventoryToResult,
    convertStoreIdToDemandwareFormat,
    getAlertText,
    storeSearchHasStock,
    updateStore,
    updateStoreGuestUser,
    getUserLocation,
} from './utils';

import './StoreDrawer.scss';
import { getStoresWhereInStock } from './getStoresWhereInStock';

export default function StoreDrawer({
    align,
    drawerHeading,
    showStock,
    toggleText,
    className,
    pageType,
}: StoreDrawerProps) {
    const {
        selectedStore: { store },
        setSelectedStore,
        setFavoriteStore,
        setStoreDrawerOpen,
        setFullStoreListResults,
        fullStoreListResults,
        storeDrawerOpen,
        setCartStoreDrawerOpen,
        cartStoreDrawerOpen,
        currentVariantId,
        currentBopisMinimumCaseQty,
        setContactUsFormStore,
    } = usePageLayout();

    const [searchedVariant, setSearchedVariant] = useState(0);
    const componentClassName = clsx('store-drawer', className);
    const { storageUserSession: { isRegistered = false } = {} } =
        useUserSession();

    const { updateItemFulfillmentMethod } = useCart();
    const experimentValue = useExperimentValue();
    const isFullSolution = experimentValue === ExperimentVersion.FULL_SOLUTION;

    const [storeListResultsToShow, setStoreListResultsToShow] = useState<
        StoreSearchResult[]
    >([]);

    const [showInventoryOnly, setShowInventoryOnly] = useState<boolean>(false);

    const [searchBoxText, setSearchBoxText] = useState<string | null>(null);
    const [pickedStore, setPickedStore] = useState<number | null>(
        store?.StoreNumber ?? null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [ranIPAutoSearch, setRanIPAutoSearch] = useState(false);

    const isPDP = pageType === PageType.PDP;
    const isCPC = pageType === PageType.CART;

    const onToggleHasItems = () => {
        setShowInventoryOnly(!showInventoryOnly);
    };

    const setInventoryOnlyStores = (stores: StoreSearchResult[]) => {
        const storesToShow = stores.filter((s: StoreSearchResult) =>
            Boolean(s.Store.HasCurrentProductInStock)
        );
        setStoreListResultsToShow(storesToShow);
    };

    useEffect(() => {
        if (showInventoryOnly) {
            setInventoryOnlyStores(storeListResultsToShow);
        } else {
            setStoreListResultsToShow(fullStoreListResults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only custom deps
    }, [showInventoryOnly, fullStoreListResults]);

    useEffect(() => {
        if (store?.StoreNumber) {
            setPickedStore(store.StoreNumber);
        }
    }, [store?.StoreNumber]);

    const allStoresURL = NAVIGATION_URLS.legacy.stores;

    const resetResults = () => {
        setIsLoading(true);
        setPickedStore(null);
        setShowAlert(false);
    };

    const finishStoreSearch = useCallback(
        (
            storeInventoryResults: InventoryHitStore[],
            storeList: StoreSearchResult[]
        ) => {
            const filteredStoreList = isPDP
                ? storeList?.filter(res => res?.Store?.isBopisEnabled)
                : storeList?.filter(res => res?.attributes?.bopisEnabled);

            if (!filteredStoreList.length) {
                setFullStoreListResults([]);
                setStoreListResultsToShow([]);
                setShowAlert(true);
                setIsLoading(false);
                return;
            }

            const storeInventory: InventoryResult[] = [
                {
                    objectID: currentVariantId,
                    sku: currentVariantId,
                    stores: storeInventoryResults,
                },
            ];

            const storesWithInventory = addStoreSkuInventoryToResult(
                storeInventory,
                filteredStoreList,
                currentVariantId,
                currentBopisMinimumCaseQty
            );

            const atLeastOneInStock = Boolean(
                storesWithInventory?.some(storeSearchHasStock)
            );

            setShowInventoryOnly(atLeastOneInStock);
            if (!atLeastOneInStock) {
                setStoreListResultsToShow(storesWithInventory);
            } else {
                setInventoryOnlyStores(storesWithInventory);
            }
            setFullStoreListResults(storesWithInventory);

            setIsLoading(false);
        },
        [
            currentBopisMinimumCaseQty,
            currentVariantId,
            setFullStoreListResults,
            isPDP,
        ]
    );

    const searchStores = useCallback(async () => {
        if (searchBoxText) {
            resetResults();

            const storeList = isCPC
                ? await getStoresByQuery(searchBoxText, 300000)
                : await getStoresByGeolocation(searchBoxText, 300000);

            const storeInventoryResults = await getStoresWhereInStock(
                isCPC ? cartStoreDrawerOpen.item?.sku : currentVariantId,
                storeList as StoreSearchResult[],
                isCPC
            );

            finishStoreSearch(
                storeInventoryResults,
                storeList as StoreSearchResult[]
            );
        }
    }, [
        cartStoreDrawerOpen.item?.sku,
        currentVariantId,
        finishStoreSearch,
        isCPC,
        searchBoxText,
    ]);

    const myLocationSearch = async () => {
        const { isGeolocationEnabled, latitude, longitude } =
            await getUserLocation();

        if (!isGeolocationEnabled && ranIPAutoSearch) {
            // TODO: handle case where user denies location data but calls myLocationSearch
            return;
        }

        setSearchBoxText(null);
        resetResults();
        let storeList: StoreSearchResult[] = [];
        if (
            isGeolocationEnabled &&
            latitude !== undefined &&
            longitude !== undefined
        ) {
            storeList = await getClosesStoresByCoordinates(latitude, longitude);
        } else if (!isGeolocationEnabled && !ranIPAutoSearch) {
            storeList = await getStoresByIPAddress();
        }
        const storeInventoryResults = await getStoresWhereInStock(
            currentVariantId,
            storeList
        );

        finishStoreSearch(storeInventoryResults, storeList);
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity -- disable
    const onDrawerSelectStore = async (pickedStoreParam?: number) => {
        // if param is sent, use that one
        // otherwise use pickedStore state
        const selectedStore = pickedStoreParam ?? pickedStore;
        if (selectedStore) {
            const pickedStoreDemandwareFormat =
                convertStoreIdToDemandwareFormat(selectedStore);
            const updateFunction = isRegistered
                ? updateStore
                : updateStoreGuestUser;
            const requestBody = {
                c_storeID: pickedStoreDemandwareFormat.toString(),
                ...(isRegistered ? {} : { domain: window?.location?.hostname }),
            };
            const requestBodyForChangeStoreCPC: UpdateItemFulfillmentMethodRequestBody =
                {
                    fulfillmentMethod: DeliveryType.BOPIS,
                    storeId: pickedStoreDemandwareFormat,
                    itemId: cartStoreDrawerOpen.item?.id ?? '',
                };
            if (
                experimentValue === ExperimentVersion.FULL_SOLUTION &&
                isCPC &&
                cartStoreDrawerOpen.item
            ) {
                await updateItemFulfillmentMethod(requestBodyForChangeStoreCPC);
            } else if (storeDrawerOpen?.pageType === 'contactUs') {
                const updatedStore = fullStoreListResults.find(
                    (s: StoreSearchResult) =>
                        s.Store.StoreNumber === selectedStore
                )?.Store;
                setContactUsFormStore(updatedStore);
            } else {
                const response = await updateFunction({
                    ...requestBody,
                    isFullSolution,
                });

                if (response?.success) {
                    const updatedStore =
                        fullStoreListResults.find(
                            (s: StoreSearchResult) =>
                                s.Store.StoreNumber === selectedStore
                        )?.Store ?? response.storeDetails;

                    setSelectedStore({
                        isLoading: false,
                        store: updatedStore,
                    });

                    setFavoriteStore({
                        isLoading: false,
                        store: updatedStore,
                    });
                }
            }

            setStoreDrawerOpen(null);
            setCartStoreDrawerOpen(prevState => ({
                ...prevState,
                open: false,
            }));
        }
    };

    const setStoresWithStock = async (storeList: StoreSearchResult[]) => {
        const storeInventoryResults = await getStoresWhereInStock(
            currentVariantId,
            storeList
        );

        const storeInventory: InventoryResult[] = [
            {
                objectID: currentVariantId,
                sku: currentVariantId,
                stores: storeInventoryResults,
            },
        ];

        const filteredStoreList = isPDP
            ? storeList?.filter(res => res?.Store?.isBopisEnabled)
            : storeList;

        const storesWithInventory = addStoreSkuInventoryToResult(
            storeInventory,
            filteredStoreList,
            currentVariantId,
            currentBopisMinimumCaseQty
        );

        setFullStoreListResults(storesWithInventory);

        // Add first store with inventory as selected store
        if (!store) {
            const firstStoreWithProduct = storesWithInventory.find(
                (s: StoreSearchResult) =>
                    Boolean(s.Store.HasCurrentProductInStock)
            );

            const storeNumber = firstStoreWithProduct?.Store.StoreNumber;

            if (storeNumber) {
                await onDrawerSelectStore(storeNumber);
            }
        }
    };

    useEffect(() => {
        // if drawer is opened and has at least one store in list that has stock, only show those stores
        if (Boolean(storeDrawerOpen) || cartStoreDrawerOpen.open) {
            const atLeastOneInStock =
                storeListResultsToShow.some(storeSearchHasStock);

            setShowInventoryOnly(atLeastOneInStock);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only custom deps
    }, [storeDrawerOpen, cartStoreDrawerOpen.open, currentVariantId]);

    useEffect(() => {
        if (!isCPC) {
            setShowAlert(storeListResultsToShow.length === 0);
        }
    }, [isCPC, storeListResultsToShow]);

    // Update stock when the product variant changes.
    useEffect(() => {
        if (searchedVariant && searchedVariant !== currentVariantId) {
            resetResults();

            const noStockResults = fullStoreListResults.map(storeResult => ({
                ...storeResult,
                Store: {
                    ...storeResult.Store,
                    CurrentProductStockstatus: StockStatusEnum.OutOfStock,
                    HasCurrentProductInStock: 0,
                },
            }));

            getStoresWhereInStock(currentVariantId, noStockResults).then(
                result => {
                    finishStoreSearch(result, noStockResults);
                    setSearchedVariant(currentVariantId);
                }
            );
        }
    }, [
        searchedVariant,
        currentVariantId,
        fullStoreListResults,
        setFullStoreListResults,
        finishStoreSearch,
    ]);

    const getInitialStoreList = useCallback(async () => {
        let storeList: StoreSearchResult[] = [];
        if (!ranIPAutoSearch) {
            storeList = await getStoresByIPAddress();
            setRanIPAutoSearch(true);
        }

        return storeList;
    }, [ranIPAutoSearch]);

    useEffect(() => {
        // PLP only code
        if (!fullStoreListResults.length && !showStock) {
            getInitialStoreList().then(storeList => {
                if (!storeList?.length) {
                    return;
                }

                setFullStoreListResults(storeList);

                if (!store) {
                    setPickedStore(storeList[0].Store.StoreNumber);
                } else {
                    setPickedStore(store.StoreNumber);
                }

                setSearchedVariant(currentVariantId);
            });
        }
    }, [
        fullStoreListResults,
        showStock,
        currentVariantId,
        store,
        setPickedStore,
        setSearchedVariant,
        setFullStoreListResults,
        getInitialStoreList,
    ]);

    useEffect(() => {
        // PDP only code. !fullStoreListResults.length and showStock are falsy on PLP
        if (!fullStoreListResults.length && showStock && currentVariantId) {
            getInitialStoreList().then(async storeList => {
                if (!storeList?.length) {
                    return;
                }

                await setStoresWithStock(storeList);
                setSearchedVariant(currentVariantId);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only custom deps
    }, [currentVariantId, fullStoreListResults.length, showStock]);

    const renderStoreCard = (s: StoreSearchResult) => {
        const { HasCurrentProductInStock: hasInventory } = s.Store;

        const timezonedDate = new Date().toLocaleString('en', {
            timeZone: s.Store.TimeZone?.TimeZoneId,
        });

        // date we are comparing to uses the timezone of the store we are using
        const currentDate = new Date(timezonedDate);

        return (
            <List.Item
                key={s.Store.StoreNumber}
                onClick={() => {
                    setPickedStore(s.Store.StoreNumber);
                }}
            >
                <StoreListingCard
                    data-testid={`store-test-${s.Store.StoreNumber}`}
                    address={{
                        addressName: s.Store.Name,
                        street: s.Store.StreetLine1,
                        city: s.Store.City,
                        state: s.Store.StateProvinceAbbreviation,
                        postalCode: s.Store.ZipPostalCode,
                    }}
                    stockStatus={s.Store.CurrentProductStockstatus}
                    showStock={showStock}
                    selected={pickedStore === s.Store.StoreNumber}
                    hasRadio
                    hasItems={hasInventory}
                    rating={undefined}
                    openText={getStoreDisplayTime(s.Store, currentDate)}
                    distance={
                        s.DistanceToStoreFromOrigin > 0
                            ? `${s.DistanceToStoreFromOrigin.toFixed(
                                  1
                              )} ${s.UnitOfDistance.replace(
                                  'kilometers',
                                  'kilometres'
                              )}`
                            : undefined
                    }
                    closed={!storeIsOpen(s.Store, currentDate)}
                    myStore={store?.StoreNumber === s.Store.StoreNumber}
                />
            </List.Item>
        );
    };

    const alertContent = getAlertText(searchBoxText ?? '');

    return (
        <StoreSearchDrawer
            viewStoresLink={allStoresURL}
            align={align}
            data-testid="store-drawer"
            className={componentClassName}
            isActive={Boolean(storeDrawerOpen) || cartStoreDrawerOpen.open}
            onClose={() => {
                setStoreDrawerOpen(null);
                setCartStoreDrawerOpen(prevState => ({
                    ...prevState,
                    open: false,
                }));
            }}
            showAlert={showAlert && !isLoading}
            showList={!showAlert && !isLoading}
            showStoreConfirmButton={pickedStore !== null}
            onUseMyLocation={myLocationSearch}
            onSearch={searchStores}
            toggleText={toggleText}
            onSelectStore={() => onDrawerSelectStore()}
            isLoading={isLoading}
            hasItems={Boolean(showInventoryOnly)}
            drawerHeading={drawerHeading}
            alertIcon="exclamation-circle-fill"
            alertHeading={alertContent?.title ?? 'No locations found'}
            placeholder={
                getLocale() === 'en-CA'
                    ? 'City or postal code'
                    : 'City, state or zip code'
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- caused by sparky pipeline issue: read more @ HCP-282
            // @ts-ignore -- caused by sparky pipeline issue: read more @ HCP-282
            alertText={
                alertContent?.text ? (
                    <>
                        {alertContent?.text}
                        <a
                            href={alertContent?.link}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {alertContent?.linkText}
                        </a>
                    </>
                ) : (
                    ''
                )
            }
            onToggleHasItems={onToggleHasItems}
            onSearchBoxChange={(val: string) => setSearchBoxText(val)}
            value={searchBoxText ?? ''}
            disableStoreConfirmButton={
                storeListResultsToShow.length === 0 || isLoading
            }
        >
            <List data-testid="store-drawer-list">
                {storeListResultsToShow.map((s: StoreSearchResult) =>
                    renderStoreCard(s)
                )}
            </List>
        </StoreSearchDrawer>
    );
}
